import { RARITY_PRESENTATION } from "@ninja/domain";
import { escapeHtml } from "./escapeHtml.js";
import { SectionTabs } from "./SectionTabs.js";
import { formatItemTier } from "./itemProgression.js";
import type { OwnedItemView, WarehouseView } from "./warehouseTypes.js";
import "./warehouse.css";

type ApiRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

interface WarehousePageDependencies {
  request: ApiRequest;
  renderOwnedItem: (item: OwnedItemView) => string;
  onOpenItem: (item: OwnedItemView) => void;
  itemCategoryLabel: (category: string) => string;
  equipmentSlotLabel: (slot: NonNullable<OwnedItemView["slot"]>) => string;
}

export class WarehousePage {
  private readonly listenerController = new AbortController();
  private sectionTabs: SectionTabs | null = null;
  private view: WarehouseView | null = null;
  private destroyed = false;

  constructor(
    private readonly content: HTMLElement,
    private readonly dependencies: WarehousePageDependencies,
  ) {}

  async mount(): Promise<void> {
    this.content.innerHTML =
      '<div class="warehouse-loading" role="status">Loading Warehouse…</div>';
    this.content.addEventListener("input", this.handleFilterChange, {
      signal: this.listenerController.signal,
    });
    this.content.addEventListener("change", this.handleFilterChange, {
      signal: this.listenerController.signal,
    });
    this.content.addEventListener("click", this.handleClick, {
      signal: this.listenerController.signal,
    });

    try {
      this.view = await this.dependencies.request<WarehouseView>("/warehouse");
      if (this.destroyed || !this.content.isConnected) return;
      this.render();
    } catch (error) {
      if (this.destroyed || !this.content.isConnected) return;
      this.renderError(error);
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.sectionTabs?.destroy();
    this.listenerController.abort();
  }

  private render(): void {
    const view = this.view;
    if (!view) return;

    const levelRows = view.warehouse.levelDefinitions
      .map((definition) => {
        const current = definition.level === view.warehouse.level;
        const currentLabel = current
          ? '<span class="building-level-current-tag">CURRENT</span>'
          : "";
        return `<tr class="${current ? "current-level" : ""}"><td>Lv. ${definition.level} ${currentLabel}</td><td>${definition.protectedPvPVolume.toLocaleString()}</td><td>Unlimited</td></tr>`;
      })
      .join("");

    this.content.innerHTML = `<div class="warehouse-page" data-warehouse-page>
      <div class="page-heading warehouse-page-heading"><div><p class="eyebrow">VILLAGE STORAGE</p><h1>Warehouse</h1></div></div>
      <div class="warehouse-overview-strip" role="group" aria-label="Warehouse summary">
        <div class="warehouse-overview-metric"><span>STORAGE CAPACITY</span><strong>${view.warehouse.storageUnlimited ? "Unlimited" : "Limited"}</strong></div>
        <div class="warehouse-overview-metric"><span>ITEMS IN STORAGE</span><strong>${view.ownedQuantity.toLocaleString()}</strong></div>
        <div class="warehouse-overview-metric"><span>CURRENT LEVEL</span><strong>${view.warehouse.level}/${view.warehouse.maxLevel}</strong><small>${view.warehouse.protectedPvPVolume.toLocaleString()} protected volume</small></div>
      </div>
      <nav class="section-tabs" role="tablist" aria-label="Warehouse sections">
        <button id="warehouse-tab-supply" class="section-tab" type="button" role="tab" aria-selected="true" aria-controls="warehouse-panel-supply" tabindex="0" data-section-tab="supply">Supply stash</button>
        <button id="warehouse-tab-effects" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="warehouse-panel-effects" tabindex="-1" data-section-tab="effects">Warehouse effects</button>
      </nav>
      <section id="warehouse-panel-supply" role="tabpanel" tabindex="0" aria-labelledby="warehouse-tab-supply" data-section-panel="supply">
        ${this.renderSupplyStash(view)}
      </section>
      <section id="warehouse-panel-effects" role="tabpanel" tabindex="0" aria-labelledby="warehouse-tab-effects" data-section-panel="effects" hidden>
        <section class="warehouse-panel">
          <div class="section-heading"><div><p class="eyebrow">LEVEL CONFIGURATION · CURRENT LEVEL ${view.warehouse.level}</p><h2>Warehouse effects</h2></div><span class="muted">Only PvP-protected volume changes by level.</span></div>
          <div class="building-level-table-wrap"><table class="building-level-table"><thead><tr><th>LEVEL</th><th>PROTECTED VOLUME</th><th>STORAGE CAPACITY</th></tr></thead><tbody>${levelRows}</tbody></table></div>
        </section>
      </section>
    </div>`;
    this.sectionTabs = new SectionTabs(this.content);
  }

  private renderSupplyStash(view: WarehouseView): string {
    const categories = [...new Set(view.items.map((item) => item.category))];
    const categoryOptions = categories
      .map(
        (category) =>
          `<option value="${escapeHtml(category)}">${this.dependencies.itemCategoryLabel(category)}</option>`,
      )
      .join("");
    const slots = [
      ...new Set(view.items.map((item) => item.slot).filter(Boolean)),
    ] as NonNullable<OwnedItemView["slot"]>[];
    const slotOptions = slots
      .map(
        (slot) =>
          `<option value="${escapeHtml(slot)}">${this.dependencies.equipmentSlotLabel(slot)}</option>`,
      )
      .join("");
    const rarities = [...new Set(view.items.map((item) => item.rarity))];
    const rarityOptions = rarities
      .map((rarity) => {
        const presentation =
          RARITY_PRESENTATION[
            rarity.toLocaleLowerCase() as keyof typeof RARITY_PRESENTATION
          ];
        return presentation
          ? `<option value="${escapeHtml(rarity)}">${escapeHtml(presentation.label)}</option>`
          : "";
      })
      .join("");
    const tiers = [...new Set(view.items.map((item) => item.tier))].sort();
    const tierOptions = tiers
      .map(
        (tier) =>
          `<option value="${tier}">${formatItemTier(tier) || "Base"}</option>`,
      )
      .join("");
    const itemList = view.items.length
      ? `<div class="owned-item-grid">${view.items.map((item) => this.dependencies.renderOwnedItem(item)).join("")}</div>`
      : '<div class="empty-state warehouse-empty"><span>▦</span><h3>Your Warehouse is empty</h3><p>Items you own will appear here when they are added to the village storage.</p></div>';

    return `<section class="warehouse-panel">
      <div class="section-heading"><div><p class="eyebrow">ITEMS YOU OWN</p><h2>Supply stash</h2></div><span class="muted">${view.ownedQuantity.toLocaleString()} ${view.ownedQuantity === 1 ? "item" : "items"}</span></div>
      <div class="warehouse-filters"><label>Search<input id="warehouse-search" type="search" placeholder="Search owned items" autocomplete="off" /></label><label>Type<select id="warehouse-category"><option value="">All types</option>${categoryOptions}</select></label><label>Slot<select id="warehouse-slot"><option value="">All slots</option>${slotOptions}</select></label><label>Rarity<select id="warehouse-rarity"><option value="">All rarities</option>${rarityOptions}</select></label><label>Tier<select id="warehouse-tier"><option value="">All tiers</option>${tierOptions}</select></label></div>
      ${itemList}
    </section>`;
  }

  private renderError(error: unknown): void {
    const message =
      error instanceof Error ? error.message : "Could not load the Warehouse.";
    this.content.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE STORAGE</p><h1>Warehouse</h1></div></div><div class="empty-state">${escapeHtml(message)}</div>`;
  }

  private handleFilterChange = (event: Event): void => {
    if (!(
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLSelectElement
    ))
      return;
    if (
      !event.target.matches(
        "#warehouse-search,#warehouse-category,#warehouse-slot,#warehouse-rarity,#warehouse-tier",
      )
    )
      return;

    const query =
      this.content
        .querySelector<HTMLInputElement>("#warehouse-search")
        ?.value.trim()
        .toLocaleLowerCase() ?? "";
    const slot =
      this.content.querySelector<HTMLSelectElement>("#warehouse-slot")?.value ??
      "";
    const category =
      this.content.querySelector<HTMLSelectElement>("#warehouse-category")
        ?.value ?? "";
    const rarity =
      this.content.querySelector<HTMLSelectElement>("#warehouse-rarity")
        ?.value ?? "";
    const tier =
      this.content.querySelector<HTMLSelectElement>("#warehouse-tier")?.value ??
      "";

    this.content
      .querySelectorAll<HTMLButtonElement>("[data-owned-item]")
      .forEach((tile) => {
        const nameMatches = (tile.dataset.name ?? "").includes(query);
        const slotMatches = !slot || tile.dataset.slot === slot;
        const categoryMatches = !category || tile.dataset.category === category;
        const rarityMatches = !rarity || tile.dataset.rarity === rarity;
        const tierMatches = !tier || tile.dataset.tier === tier;
        tile.hidden = !(
          nameMatches &&
          slotMatches &&
          categoryMatches &&
          rarityMatches &&
          tierMatches
        );
      });
  };

  private handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    const tile = event.target.closest<HTMLButtonElement>("[data-owned-item]");
    if (!tile || !this.content.contains(tile)) return;
    const item = this.view?.items.find(
      (entry) => entry.id === tile.dataset.ownedItem,
    );
    if (item) this.dependencies.onOpenItem(item);
  };
}
